export interface DetailedBullet {
  title: string
  detail: string
}

export interface Bonus {
  id: string
  title: string
  subtitle: string
  /** Reference value in USD cents, shown struck through next to "Included free" */
  referenceValueCents?: number
  intro?: string
  bulletsIntro?: string
  bullets?: string[]
  detailedBullets?: DetailedBullet[]
  secondaryBulletsIntro?: string
  secondaryBullets?: string[]
  secondaryNote?: string
  systemLabel?: string
  system?: string
  result?: string
  closing?: string[]
}

export const bonuses: Bonus[] = [
  {
    id: '01',
    title: 'Electrical Formulas, Ohm’s Law & Wiring Quick-Reference Charts',
    subtitle: 'The Right Formula and the Right Reference at Every Bench',
    referenceValueCents: 14990,
    intro:
      'Printable quick-reference charts for electrical instructors and students. Each chart is clean, labeled and ready to post in the electrical lab or hand out in class. Always follow your local electrical code and the manufacturer data.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Ohm’s law & the power formula wheel with worked examples',
      'Series & parallel circuit rules',
      'Single-phase & three-phase power formulas',
      'Voltage drop basics',
      'Conductor color codes: NEC (US) & IEC / BS 7671',
      'Typical NEC breaker & wire size pairings',
      'GFCI vs. AFCI vs. RCD uses',
      'Electrical symbols, metric prefixes & unit conversions',
      'Multimeter function guides',
    ],
    result: 'Help students solve circuits and choose the right reference with confidence, and spend less time guessing.',
  },
  {
    id: '02',
    title: 'Electrical Demonstration Lesson Scripts',
    subtitle: 'Run a Polished Demonstration Without Preparing From Scratch',
    referenceValueCents: 9990,
    intro:
      'Ready-to-read demonstration scripts that follow the 4-step method (show, explain, student explains, student does), each with timing, materials and PPE list, safety reminders, key instructor phrases, common student mistakes and a quick check at the end.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Electrical safety & lockout/tagout briefing',
      'Test before touch: live-dead-live',
      'Measuring voltage, current, resistance & continuity',
      'Series & parallel circuits on a training board',
      'Single-pole & three-way switch circuits',
      'Wiring a receptacle & testing a GFCI',
      'Three-phase motor starter with start/stop push buttons',
      'Systematic troubleshooting demo',
    ],
    result: 'Teach every demonstration with the confidence of a script that already works.',
  },
  {
    id: '03',
    title: 'Circuit-Building Lab Projects & Wiring Exercise Workbook',
    subtitle: 'Structured Lab Practice, Week After Week',
    referenceValueCents: 7990,
    intro:
      'A practice workbook of hands-on lab exercises, all done on de-energized training boards and low-voltage trainers. Each exercise has the objective, materials, wiring diagram, step-by-step instructions, safety checks before energizing (instructor sign-off), expected readings, a checklist and space for instructor feedback.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Series, parallel & combination circuits with measurements',
      'Lamp & switch circuits',
      'Three-way & four-way switching',
      'Receptacle & GFCI circuits',
      'A small residential panel layout',
      'Relay & contactor control circuits',
      'Motor start/stop with seal-in',
      'Simple PLC ladder exercises',
    ],
    result: 'Give students the repetition they need to build neat, correct and safely tested circuits.',
  },
  {
    id: '04',
    title: 'Electrical Symbols & Fault-Finding Flashcards',
    subtitle: 'Train Students to Read Every Symbol and Diagnose Every Fault',
    referenceValueCents: 6990,
    intro:
      'Printable visual flashcards: schematic and wiring symbols with ANSI/NEMA and IEC versions labeled, plus fault cards with the symptom and a labeled diagram on the front, and on the back the likely causes, the safe test sequence with the multimeter (with PPE and lockout/tagout), the fix, an instructor cue and a quick-check question.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Switches, receptacles, lamps, fuses & breakers',
      'Relays, contactors, overloads & motors',
      'Transformers, sensors & PLC inputs/outputs',
      'Open circuits & short circuits',
      'Ground faults & high-resistance connections',
      'Tripped breakers, blown fuses & failed GFCIs',
      'Motor overload trips & single-phasing',
      'Failed contactor coils & wrong VFD parameters',
    ],
    result: 'Use them for warm-ups, symbol games, fault-diagnosis practice and quizzes.',
  },
  {
    id: '05',
    title: 'Electrical Competency Checklists, Rubrics & Certificates',
    subtitle: 'Evaluate Objectively and Document Every Skill Achieved',
    referenceValueCents: 5990,
    intro:
      'Ready-to-print assessment tools for electrical instructors and technical schools: beginner, intermediate and advanced competency checklists, scoring rubrics for practical lab tasks and wiring projects, a student skills-progress tracker and editable course-completion certificates for each level.',
    bulletsIntro: 'Includes:',
    bullets: [
      'Safety, PPE & lockout/tagout',
      'Meter use & circuit building',
      'Residential wiring & circuit protection',
      'Motor controls & diagram reading',
      'Troubleshooting & PLC basics',
      'Lab task & wiring project rubrics',
      'Skills-progress tracker',
      'Course-completion certificates',
    ],
    result: 'Assess every student fairly and give structured feedback at every level.',
  },
]
